import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import TablePagination, { TablePaginationProps } from "@material-ui/core/TablePagination";
import CustomWarningIcon from "../../../assets/icons/CustomWarningIcon";
import HistoricalTestCaseResponse from "../../../api/models/HistoricalTestCaseResponse";
import { dashToSlash } from "../../../api/services/common/FormatDateService";
import CustomTable, { ColumnFilterObject, CustomTableRowData } from "../../../components/CustomTable";
import DeepLink from "../../../components/DeepLink";
import LinearIndeterminate from "../../../components/Loading/LinearIndeterminate";

import { TestSeriesTabType } from "../../../contexts/TestSeriesContext";
import { TestRequestTabType } from "../useTestRequestParams";
import useStyles from "./AnalyzeHistoricalResultsTableStyle";
import CustomButton from "../../../components/Button/CustomButton";
import historicalResultsTableColumnMap from "./TabbedDialog/TestCasesTab/utils/HistoricalResultsTableColumnMap";
import StyledTooltip from "../../../components/Wrapper/StyledTooltip";

type ColumnsWithAction = typeof WITH_ACTION[number];

interface AnalyzeHistoricalResultsTableProps {
  loading: boolean;
  onProductCellClick: (sampleId: number, requestId: string) => void;
  onRowSelectionChange: (selected: boolean, rowData: CustomTableRowData) => void;
  onSelectAllChange: (checked: boolean, activeRows: HistoricalTestCaseResponse[]) => void;
  onSubstrateCellClick: (sampleId: number, requestId: string) => void;
  results: HistoricalTestCaseResponse[];
  selectedRows: Record<number, boolean>;
  TablePaginationProps: Pick<
    TablePaginationProps,
    "count" | "onChangePage" | "onChangeRowsPerPage" | "page" | "rowsPerPage"
  >;
}

const getTypedKeys = Object.keys as <O extends object>(obj: O) => Array<keyof O>;
const COLUMNS_ORDERED_DICTIONARY = {
  testSeries: "Series ID",
  firstSubstrateSample: "Substrate",
  secondSubstrateSample: "Substrate 2",
  productSample: "Product",
  testMethodName: "Test Method",
  buildingAction: "Building Action",
  treatment: "Treatment",
  condition: "Test Condition",
  completed: "Completed",
} as const;

const COLUMNS_ORDERED = getTypedKeys(COLUMNS_ORDERED_DICTIONARY);
const EMPTY_RESULT = "__NONE__";
const WITH_ACTION = ["firstSubstrateSample", "secondSubstrateSample", "productSample"] as const;

function generateRows(testCases: HistoricalTestCaseResponse[]): any[] {
  return testCases.map((tc) => ({
    testSeries: (
      <Box display="flex" alignItems="center">
        <DeepLink
          label={`${tc.testSeries.requestId}-${tc.testSeries.indexNumber}-${tc.indexNumber}`}
          pathParams={{
            requestId: tc.testSeries.requestId,
            tab: TestRequestTabType.TEST,
          }}
          searchParams={{
            testSeriesId: String(tc.testSeries.id),
            tab: String(TestSeriesTabType.TestTable),
          }}
          LinkProps={{ rel: "noopener noreferrer", target: "_blank" }}
        />
        {tc.testSeries.confidentiality === "HIGHLY_CONFIDENTIAL" && (
          <StyledTooltip title="This testcase is from a secret project!" placement="top">
            <CustomWarningIcon />
          </StyledTooltip>
        )}
        {tc.testSeries.confidentiality === "NORMAL_CONFIDENTIAL" && (
          <StyledTooltip title="This testcase is from a confidential request!" placement="top">
            <CustomWarningIcon />
          </StyledTooltip>
        )}
      </Box>
    ),
    firstSubstrateSample: tc.firstSubstrateSample?.name,
    secondSubstrateSample: tc.secondSubstrateSample?.name,
    productSample: tc.productSample.name,
    testMethodName: tc.testSeries.testMethodName,
    buildingAction: maskEmpty(tc.buildingActionFlow.name, EMPTY_RESULT),
    treatment: maskEmpty(tc.treatmentActionFlow.name, EMPTY_RESULT),
    condition: maskEmpty(tc.conditionFlow.name, EMPTY_RESULT),
    completed: dashToSlash(tc.testSeries.completionDate),
    id: tc.id,
  }));
}

function maskEmpty(input: string, emptyMarker: string, replacement: string = "") {
  return input.replace(emptyMarker, replacement);
}

function getActionColumIndexes(
  columnsOrdered: Array<keyof typeof COLUMNS_ORDERED_DICTIONARY>,
  actionColumns: ReadonlyArray<ColumnsWithAction>,
) {
  if (!columnsOrdered.length) return [];
  return actionColumns.reduce<number[]>((indexes, currAc) => {
    const index = columnsOrdered.indexOf(currAc);
    if (index > -1) indexes.push(index);
    return indexes;
  }, []);
}

function AnalyzeHistoricalResultsTable({
  loading,
  onProductCellClick: handleProductCellClick,
  onRowSelectionChange: handleRowSelectionChange,
  onSelectAllChange: handleSelectAllChange,
  onSubstrateCellClick: handleSubstrateCellClick,
  results,
  selectedRows,
  TablePaginationProps,
}: AnalyzeHistoricalResultsTableProps) {
  const classes = useStyles();
  const rows = generateRows(results);

  const [resetColumnFilters, setResetColumnFilters] = useState(false);

  const handleTableAction = (_: CustomTableRowData, key: string, rowIndex: number) => {
    const data = results[rowIndex];
    const sampleId = data?.[key as ColumnsWithAction]?.id;
    const requestId = data?.testSeries?.requestId;
    if (sampleId && requestId) {
      switch (key) {
        case "firstSubstrateSample":
        case "secondSubstrateSample":
          handleSubstrateCellClick(sampleId, requestId);
          break;
        case "productSample":
          handleProductCellClick(sampleId, requestId);
          break;
        default:
          break;
      }
    }
  };

  const historicalColumnFilterObject: ColumnFilterObject = {
    columnFilterMap: historicalResultsTableColumnMap,
    allowColumnFilters: true,
    resetColumnFilters,
    setResetColumnFilters,
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <CustomButton primary onClick={() => setResetColumnFilters(true)} text="Remove all filters" />
      </Box>
      <Box>
        <LinearIndeterminate loading={loading} />
        <CustomTable
          actionColumnIndexes={getActionColumIndexes(COLUMNS_ORDERED, WITH_ACTION)}
          checkboxSelection
          className={classes.table}
          columns={Object.values(COLUMNS_ORDERED_DICTIONARY)}
          columnMaxWidths={{
            [COLUMNS_ORDERED_DICTIONARY.firstSubstrateSample]: "16ch",
            [COLUMNS_ORDERED_DICTIONARY.secondSubstrateSample]: "16ch",
            [COLUMNS_ORDERED_DICTIONARY.testMethodName]: "18ch",
          }}
          excludedKeys={["id", "testSeriesLabel"]}
          handleEdit={handleTableAction}
          headDense
          onCheckboxChange={handleRowSelectionChange}
          onSelectAllChange={handleSelectAllChange}
          rows={rows}
          selectedRows={selectedRows}
          columnFilterObject={historicalColumnFilterObject}
        />
        <TablePagination component="div" {...TablePaginationProps} />
      </Box>
    </>
  );
}

export default AnalyzeHistoricalResultsTable;
